import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  ChevronLeft, Type, Square, Circle as CircleIcon, Minus, Image as ImageIcon, Undo2, Redo2,
  Copy, Trash2, Eye, ZoomIn, ZoomOut, Maximize2, Save, Search, Layers, Plus, GraduationCap,
  Users, Building2, CalendarDays, Wallet, Code, AlignLeft, AlignCenter, AlignRight, Bold, Italic,
  Underline as UnderlineIcon, UploadCloud, IdCard, Loader2, Grid3x3,
  AlignCenterHorizontal, AlignCenterVertical, BringToFront, SendToBack, Table2,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";

const PX = 3.7795; // px per mm
const CATEGORIES = ["Academic", "Admit Card", "Birthday Card", "Bonafide", "Certificate", "Character Certificate", "Fee Receipt", "Festival Banner", "General", "Id Card", "Interactive", "Marksheet", "Staff Id Card", "Transfer Certificate"];
const SIZES = [
  { label: "CR80 Portrait — 54 × 85.6", w: 54, h: 85.6 },
  { label: "CR80 Landscape — 85.6 × 54", w: 85.6, h: 54 },
  { label: "A4 Portrait — 210 × 297", w: 210, h: 297 },
  { label: "A4 Landscape — 297 × 210", w: 297, h: 210 },
  { label: "A5 Portrait — 148 × 210", w: 148, h: 210 },
  { label: "A6 Landscape — 148 × 105", w: 148, h: 105 },
  { label: "Badge — 100 × 150", w: 100, h: 150 },
];
const ICONS = { image: ImageIcon, graduation: GraduationCap, users: Users, building: Building2, calendar: CalendarDays, wallet: Wallet };
const IMG_FIELDS = ["logo", "student_photo", "qr_code", "attendance_qr", "principal_signature"];
const BG_SWATCHES = ["#ffffff", "#f8fafc", "#eef2ff", "#fdf2f8", "#fef9c3", "#fee2e2", "#0f172a", "#1e3a8a", "#0891b2", "#15803d", "#7c3aed"];
const OVERRIDES = { dob: "Date of Birth", roll_no: "Roll No.", admission_no: "Admission No.", qr_code: "QR Code (verify)", attendance_qr: "Attendance QR", student_photo: "Student Photo", logo: "School Logo", principal_signature: "Principal Signature", class: "Class & Section", national_id: "National ID", pen_number: "PEN Number", apaar_id: "APAAR ID", total_fees: "Total Fees", amount_paid: "Amount Paid", balance_due: "Balance Due", fees_in_words: "Fees In Words" };
const SAMPLE = { student_name: "Aarav Sharma", first_name: "Aarav", last_name: "Sharma", admission_no: "ADM-1024", roll_no: "12", class: "Grade 5", section: "A", dob: "05-01-2015", gender: "Male", blood_group: "O+", category: "General", nationality: "Indian", house: "Blue House", address: "45 Green Park", city: "Delhi", state: "Delhi", pin_code: "110016", student_phone: "9876500000", father_name: "Rohit Sharma", father_phone: "9876500001", mother_name: "Priya Sharma", mother_phone: "9876500002", guardian_name: "Rohit Sharma", academic_session: "2026-27", class_teacher: "Mrs. Nisha Verma", school_name: "SSS Public School", school_address: "123 School Lane, City", school_phone: "011-2345678", school_email: "info@sssps.edu", udise: "09010100101", school_code: "SSS001", affiliation_no: "AFF123456", principal_name: "Dr. S. K. Rao", website: "www.sssps.edu", tag_line: "Knowledge · Discipline · Excellence", total_fees: "45,000", amount_paid: "30,000", balance_due: "15,000", current_date: new Date().toLocaleDateString("en-GB"), current_year: String(new Date().getFullYear()), issue_date: new Date().toLocaleDateString("en-GB") };

const humanize = (t) => OVERRIDES[t] || t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const uid = () => Math.random().toString(36).slice(2, 9);
const clone = (o) => JSON.parse(JSON.stringify(o));
const QR_SRC = (data) => `${process.env.REACT_APP_BACKEND_URL}/api/qr?data=${encodeURIComponent(data)}`;
const EFFECT = (el) => {
  const c = el.effectColor || "#000000";
  if (el.effect === "shadow") return `2px 2px 3px ${c}66`;
  if (el.effect === "outline") return `-1px -1px 0 ${c},1px -1px 0 ${c},-1px 1px 0 ${c},1px 1px 0 ${c}`;
  return "none";
};
const FONT_FACES = [
  "Arial", "Helvetica", "Georgia", "Times New Roman", "Garamond", "Palatino Linotype", "Trebuchet MS", "Courier New",
  "Playfair Display", "Cinzel", "EB Garamond", "Cormorant Garamond", "Merriweather", "Libre Baskerville", "Lora", "PT Serif",
  "Great Vibes", "Pinyon Script", "Tangerine", "Dancing Script", "Parisienne", "Allura",
  "Montserrat", "Poppins", "Raleway", "Oswald",
];

export default function CanvasDesigner() {
  const nav = useNavigate();
  const { id } = useParams();
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Id Card");
  const [size, setSize] = useState({ w: 54, h: 85.6 });
  const [bg, setBg] = useState("#ffffff");
  const [doc, setDoc] = useState({ front: [], back: [] });
  const [side, setSide] = useState("front");
  const [sel, setSel] = useState(null);
  const [guides, setGuides] = useState({ x: [], y: [] });
  const [zoom, setZoom] = useState(1.6);
  const [preview, setPreview] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [tgrid, setTgrid] = useState({ r: 0, c: 0 });
  const [tableSel, setTableSel] = useState(null);
  const [tab, setTab] = useState("fields");
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);

  const docRef = useRef(doc);
  useEffect(() => { docRef.current = doc; }, [doc]);
  const sideRef = useRef(side);
  useEffect(() => { sideRef.current = side; }, [side]);
  const sizeRef = useRef(size);
  useEffect(() => { sizeRef.current = size; }, [size]);
  const hist = useRef({ stack: [{ front: [], back: [] }], idx: 0 });
  const drag = useRef(null);

  const pushHist = (d) => {
    const h = hist.current;
    h.stack = h.stack.slice(0, h.idx + 1);
    h.stack.push(clone(d));
    h.idx = h.stack.length - 1;
  };
  const mutate = (fn) => setDoc((prev) => { const next = fn(prev); pushHist(next); return next; });
  const undo = () => { const h = hist.current; if (h.idx > 0) { h.idx--; setDoc(clone(h.stack[h.idx])); setSel(null); } };
  const redo = () => { const h = hist.current; if (h.idx < h.stack.length - 1) { h.idx++; setDoc(clone(h.stack[h.idx])); setSel(null); } };

  useEffect(() => {
    api.get("/templates/placeholders").then((r) => setGroups(r.data.groups || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!id) return;
    api.get(`/templates/${id}`).then((r) => {
      setName(r.data.name || ""); setCategory(r.data.category || "Id Card");
      const c = r.data.canvas;
      if (c) {
        if (c.size) setSize(c.size); if (c.bg) setBg(c.bg);
        const d = { front: c.front || [], back: c.back || [] };
        setDoc(d); hist.current = { stack: [clone(d)], idx: 0 };
      }
    }).catch((e) => { toast.error(apiErr(e.response?.data?.detail)); nav("/templates"); })
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const curr = doc[side];
  const setCurrLive = (arr) => setDoc((prev) => ({ ...prev, [side]: arr }));
  const addEl = (el) => { mutate((prev) => ({ ...prev, [side]: [...prev[side], el] })); setSel(el.id); };
  const updateEl = (elId, patch, record = true) => {
    const fn = (prev) => ({ ...prev, [side]: prev[side].map((e) => (e.id === elId ? { ...e, ...patch } : e)) });
    record ? mutate(fn) : setDoc(fn);
  };
  const deleteEl = (elId) => { mutate((prev) => ({ ...prev, [side]: prev[side].filter((e) => e.id !== elId) })); setSel(null); };
  const duplicateEl = (elId) => {
    const src = curr.find((e) => e.id === elId); if (!src) return;
    const cp = { ...clone(src), id: uid(), x: src.x + 10, y: src.y + 10 };
    mutate((prev) => ({ ...prev, [side]: [...prev[side], cp] })); setSel(cp.id);
  };
  const centerH = () => { const el = curr.find((e) => e.id === sel); if (el) updateEl(sel, { x: Math.round((size.w * PX - el.w) / 2) }); };
  const centerV = () => { const el = curr.find((e) => e.id === sel); if (el) updateEl(sel, { y: Math.round((size.h * PX - el.h) / 2) }); };
  const reorder = (dir) => {
    if (!sel) return;
    mutate((prev) => {
      const arr = [...prev[side]]; const i = arr.findIndex((e) => e.id === sel);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= arr.length) return prev;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...prev, [side]: arr };
    });
  };
  const addTable = (rows, cols) => {
    addEl({ id: uid(), type: "table", x: 14, y: 14, w: Math.min(260, cols * 44), h: rows * 26, rows, cols, border: true, cells: {} });
    setTgrid({ r: 0, c: 0 });
  };
  const addDynTable = (kind) => {
    addEl({ id: uid(), type: "table", dynamic: kind, x: 14, y: 14, w: 320, h: 120, border: true, headerBg: "#e2e8f0", fontSize: 11 });
  };
  const toggleTableBorder = () => {
    const id = (selEl && selEl.type === "table") ? selEl.id : (tableSel && tableSel.id);
    if (!id) { toast.error("Select a table first"); return; }
    const el = curr.find((e) => e.id === id);
    if (el) updateEl(id, { border: !el.border });
  };
  const mergeTableCells = () => {
    if (!tableSel) { toast.error("Select cells in a table"); return; }
    const { id, r0, c0, r1, c1 } = tableSel;
    const rA = Math.min(r0, r1), rB = Math.max(r0, r1), cA = Math.min(c0, c1), cB = Math.max(c0, c1);
    if (rA === rB && cA === cB) { toast.error("Select 2 or more cells to merge"); return; }
    const el = curr.find((e) => e.id === id); if (!el) return;
    const cells = { ...(el.cells || {}) };
    const fk = `${rA}_${cA}`; const first = { ...(cells[fk] || {}) };
    let text = first.text || "";
    for (let r = rA; r <= rB; r++) for (let c = cA; c <= cB; c++) {
      const k = `${r}_${c}`; if (k === fk) continue;
      if (cells[k]?.text) text += " " + cells[k].text;
      cells[k] = { ...(cells[k] || {}), hidden: true, cs: 1, rs: 1 };
    }
    cells[fk] = { ...first, text, cs: cB - cA + 1, rs: rB - rA + 1, hidden: false };
    updateEl(id, { cells }); setTableSel(null);
  };
  const onCellClick = (id, r, c, shift) => {
    setSel(id);
    setTableSel((prev) => (shift && prev && prev.id === id) ? { ...prev, r1: r, c1: c } : { id, r0: r, c0: c, r1: r, c1: c });
  };
  const setCellText = (id, r, c, text) => {
    const el = curr.find((e) => e.id === id); if (!el) return;
    const cells = { ...(el.cells || {}) };
    cells[`${r}_${c}`] = { ...(cells[`${r}_${c}`] || {}), text };
    updateEl(id, { cells });
  };
  const tableRowCol = (action) => {
    const id = (selEl && selEl.type === "table" && !selEl.dynamic) ? selEl.id : (tableSel && tableSel.id);
    if (!id) { toast.error("Select a table first"); return; }
    const el = curr.find((e) => e.id === id); if (!el || el.dynamic) return;
    const selR = (tableSel && tableSel.id === id) ? tableSel.r0 : el.rows - 1;
    const selC = (tableSel && tableSel.id === id) ? tableSel.c0 : el.cols - 1;
    let rows = el.rows, cols = el.cols;
    const cells = {}; const old = el.cells || {};
    if (action === "addRow") {
      rows += 1; const at = selR + 1;
      Object.entries(old).forEach(([k, v]) => { const [r, c] = k.split("_").map(Number); cells[`${r >= at ? r + 1 : r}_${c}`] = v; });
    } else if (action === "delRow") {
      if (el.rows <= 1) { toast.error("At least one row is required"); return; }
      rows -= 1; const at = selR;
      Object.entries(old).forEach(([k, v]) => { const [r, c] = k.split("_").map(Number); if (r === at) return; cells[`${r > at ? r - 1 : r}_${c}`] = v; });
    } else if (action === "addCol") {
      cols += 1; const at = selC + 1;
      Object.entries(old).forEach(([k, v]) => { const [r, c] = k.split("_").map(Number); cells[`${r}_${c >= at ? c + 1 : c}`] = v; });
    } else if (action === "delCol") {
      if (el.cols <= 1) { toast.error("At least one column is required"); return; }
      cols -= 1; const at = selC;
      Object.entries(old).forEach(([k, v]) => { const [r, c] = k.split("_").map(Number); if (c === at) return; cells[`${r}_${c > at ? c - 1 : c}`] = v; });
    }
    const h = Math.max(12, Math.round((el.h / el.rows) * rows));
    const w = Math.max(20, Math.round((el.w / el.cols) * cols));
    updateEl(id, { rows, cols, cells, h, w });
    setTableSel(null);
  };

  const base = (type, extra) => ({ id: uid(), type, x: 12, y: 12, w: 140, h: 28, ...extra });
  const addText = () => addEl(base("text", { text: "Text", fontSize: 14, color: "#111827", bold: false, italic: false, underline: false, align: "left", fontFamily: "Arial" }));
  const addRect = () => addEl(base("rect", { w: 120, h: 60, fill: "#e5e7eb", borderColor: "#94a3b8", borderWidth: 1, radius: 4 }));
  const addCircle = () => addEl(base("circle", { w: 70, h: 70, fill: "#e5e7eb", borderColor: "#94a3b8", borderWidth: 1 }));
  const addLine = () => addEl(base("line", { w: 160, h: 2, fill: "#111827" }));
  const addImage = (src) => addEl(base("image", { w: 90, h: 90, src: src || "", fieldImage: "" }));
  const addField = (tag) => {
    if (IMG_FIELDS.includes(tag)) addEl(base("image", { w: 90, h: 90, src: "", fieldImage: tag }));
    else addEl(base("text", { text: `{${humanize(tag)}}`, field: tag, fontSize: 13, color: "#111827", bold: false, italic: false, underline: false, align: "left", fontFamily: "Arial" }));
  };

  const onUpload = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3MB)"); return; }
    const r = new FileReader(); r.onload = () => addImage(r.result); r.readAsDataURL(file);
  };

  // drag / resize
  const startDrag = (e, el, mode) => {
    if (preview) return;
    e.stopPropagation(); setSel(el.id);
    drag.current = { id: el.id, mode, sx: e.clientX, sy: e.clientY, ox: el.x, oy: el.y, ow: el.w, oh: el.h };
  };
  const RESIZE_HANDLES = [
    ["nw", { left: -5, top: -5 }, "nwse-resize"],
    ["ne", { right: -5, top: -5 }, "nesw-resize"],
    ["sw", { left: -5, bottom: -5 }, "nesw-resize"],
    ["se", { right: -5, bottom: -5 }, "nwse-resize"],
  ];
  const cornerHandles = (el) => RESIZE_HANDLES.map(([dir, pos, cursor]) => (
    <span key={dir} data-testid={`cv-resize-${dir}`} onMouseDown={(e) => startDrag(e, el, "resize-" + dir)}
      style={{ position: "absolute", width: 11, height: 11, background: "#fff", border: "1.5px solid #6366f1", borderRadius: 2, cursor, zIndex: 30, ...pos }} />
  ));
  useEffect(() => {
    const SNAP = 6;
    const move = (e) => {
      const d = drag.current; if (!d) return;
      const dx = (e.clientX - d.sx) / zoom, dy = (e.clientY - d.sy) / zoom;
      if (d.mode.startsWith("resize")) {
        const dir = d.mode.slice(7) || "se";
        let nx = d.ox, ny = d.oy, nw = d.ow, nh = d.oh;
        if (dir.includes("e")) nw = Math.max(10, Math.round(d.ow + dx));
        if (dir.includes("s")) nh = Math.max(6, Math.round(d.oh + dy));
        if (dir.includes("w")) { nw = Math.max(10, Math.round(d.ow - dx)); nx = Math.round(d.ox + d.ow - nw); }
        if (dir.includes("n")) { nh = Math.max(6, Math.round(d.oh - dy)); ny = Math.round(d.oy + d.oh - nh); }
        updateEl(d.id, { x: nx, y: ny, w: nw, h: nh }, false);
        return;
      }
      let nx = Math.round(d.ox + dx), ny = Math.round(d.oy + dy);
      const sd = sizeRef.current, bw = sd.w * PX, bh = sd.h * PX;
      const els = (docRef.current[sideRef.current] || []).filter((x) => x.id !== d.id);
      const vt = [0, bw / 2, bw], ht = [0, bh / 2, bh];
      els.forEach((o) => { vt.push(o.x, o.x + o.w / 2, o.x + o.w); ht.push(o.y, o.y + o.h / 2, o.y + o.h); });
      let bx = null;
      [[nx, 0], [nx + d.ow / 2, d.ow / 2], [nx + d.ow, d.ow]].forEach(([p, off]) => vt.forEach((t) => { if (Math.abs(p - t) <= SNAP) { const c = t - off; if (bx === null || Math.abs(c - nx) < Math.abs(bx - nx)) bx = c; } }));
      if (bx !== null) nx = Math.round(bx);
      let by = null;
      [[ny, 0], [ny + d.oh / 2, d.oh / 2], [ny + d.oh, d.oh]].forEach(([p, off]) => ht.forEach((t) => { if (Math.abs(p - t) <= SNAP) { const c = t - off; if (by === null || Math.abs(c - ny) < Math.abs(by - ny)) by = c; } }));
      if (by !== null) ny = Math.round(by);
      const gx = [], gy = [];
      [nx, nx + d.ow / 2, nx + d.ow].forEach((p) => vt.forEach((t) => { if (Math.abs(p - t) < 0.75) gx.push(t); }));
      [ny, ny + d.oh / 2, ny + d.oh].forEach((p) => ht.forEach((t) => { if (Math.abs(p - t) < 0.75) gy.push(t); }));
      updateEl(d.id, { x: nx, y: ny }, false);
      setGuides({ x: [...new Set(gx)], y: [...new Set(gy)] });
    };
    const up = () => { if (drag.current) { drag.current = null; pushHist(docRef.current); setGuides({ x: [], y: [] }); } };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, [zoom]); // eslint-disable-line

  const selEl = curr.find((e) => e.id === sel) || null;
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); }
      else if (!sel) return;
      else if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); deleteEl(sel); }
      else if (e.key.startsWith("Arrow")) {
        e.preventDefault(); const el = curr.find((x) => x.id === sel); if (!el) return;
        const d = e.shiftKey ? 10 : 1;
        const p = { ArrowLeft: { x: el.x - d }, ArrowRight: { x: el.x + d }, ArrowUp: { y: el.y - d }, ArrowDown: { y: el.y + d } }[e.key];
        updateEl(sel, p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel, curr]); // eslint-disable-line

  const applyLayout = (kind) => {
    const W = size.w * PX;
    const front = [];
    if (kind === "header") front.push({ id: uid(), type: "rect", x: 0, y: 0, w: Math.round(W), h: 60, fill: "#4338ca", borderWidth: 0, radius: 0 });
    if (kind === "photo-top") front.push({ id: uid(), type: "image", x: Math.round(W / 2 - 40), y: 14, w: 80, h: 80, src: "", fieldImage: "student_photo" });
    if (kind === "photo-left") front.push({ id: uid(), type: "image", x: 12, y: 14, w: 70, h: 80, src: "", fieldImage: "student_photo" });
    front.push({ id: uid(), type: "text", text: "{Student Name}", field: "student_name", x: 12, y: kind === "photo-top" ? 104 : 104, w: Math.round(W - 24), h: 22, fontSize: 15, bold: true, color: kind === "header" ? "#111827" : "#111827", align: "center", fontFamily: "Arial" });
    front.push({ id: uid(), type: "text", text: "{Class & Section}", field: "class", x: 12, y: 130, w: Math.round(W - 24), h: 18, fontSize: 12, color: "#475569", align: "center", fontFamily: "Arial" });
    mutate((prev) => ({ ...prev, front })); setSide("front"); setSel(null);
  };
  const addBlock = (kind) => {
    const items = [];
    const mk = (type, extra) => ({ id: uid(), type, x: 14, y: 14, w: 150, h: 20, ...extra });
    if (kind === "name-class") { items.push(mk("text", { text: "{Student Name}", field: "student_name", y: 14, fontSize: 15, bold: true, fontFamily: "Arial" })); items.push(mk("text", { text: "{Class & Section}", field: "class", y: 40, fontSize: 12, color: "#475569", fontFamily: "Arial" })); }
    if (kind === "id-details") { ["admission_no", "roll_no", "dob"].forEach((f, i) => items.push(mk("text", { text: `{${humanize(f)}}`, field: f, y: 14 + i * 24, fontSize: 12, fontFamily: "Arial" }))); }
    if (kind === "photo-name") { items.push(mk("image", { y: 14, w: 70, h: 80, fieldImage: "student_photo" })); items.push(mk("text", { text: "{Student Name}", field: "student_name", x: 92, y: 30, fontSize: 14, bold: true, fontFamily: "Arial" })); }
    if (kind === "qr-caption") { items.push(mk("image", { y: 14, w: 70, h: 70, fieldImage: "qr_code" })); items.push(mk("text", { text: "Scan to verify", x: 14, y: 90, fontSize: 11, color: "#64748b", fontFamily: "Arial" })); }
    mutate((prev) => ({ ...prev, [side]: [...prev[side], ...items] })); setSel(null);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Enter a template name"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), category, canvas: { size, bg, front: doc.front, back: doc.back } };
      if (id) {
        await api.put(`/templates/${id}`, payload);
      } else {
        const r = await api.post("/templates", payload);
        if (r.data?.id) nav(`/templates/${r.data.id}/designer`, { replace: true });
      }
      toast.success("Design saved");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const valueOf = (el) => (preview ? (el.field ? (SAMPLE[el.field] ?? `{${humanize(el.field)}}`) : el.text) : el.text);

  const filtered = groups.map((g) => ({ ...g, tags: g.tags.filter((t) => !q.trim() || humanize(t).toLowerCase().includes(q.trim().toLowerCase()) || t.includes(q.trim().toLowerCase())) })).filter((g) => g.tags.length);
  const baseW = size.w * PX, baseH = size.h * PX;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const renderTable = (el) => {
    const selected = el.id === sel && !preview;
    if (el.dynamic) {
      const marks = el.dynamic === "marks";
      const headers = marks ? ["Subject", "Max", "Marks", "Grade", "Result"] : ["Subject", "Date", "Day", "Timing"];
      const sample = marks
        ? [["English", "100", "82", "A2", "Pass"], ["Mathematics", "100", "91", "A1", "Pass"], ["Science", "100", "74", "B1", "Pass"]]
        : [["English", "15-07-2026", "Wednesday", "09:00 - 11:00"], ["Mathematics", "17-07-2026", "Friday", "09:00 - 11:30"], ["Science", "19-07-2026", "Sunday", "09:00 - 11:00"]];
      const bd = el.border ? "1px solid #334155" : "1px solid transparent";
      return (
        <div key={el.id} data-testid={`cv-el-${el.id}`} style={{ position: "absolute", left: el.x, top: el.y, width: el.w, height: el.h, outline: selected ? "1.5px solid #6366f1" : "none", boxSizing: "border-box" }}
          onMouseDown={(e) => startDrag(e, el, "move")} onClick={(e) => { e.stopPropagation(); if (!preview) setSel(el.id); }}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: el.fontSize || 11 }}>
            <tbody>
              <tr>{headers.map((h) => <td key={h} style={{ border: bd, padding: 3, fontWeight: 700, background: el.headerBg || "#e2e8f0" }}>{h}</td>)}</tr>
              {sample.map((row, ri) => <tr key={ri}>{row.map((c, ci) => <td key={ci} style={{ border: bd, padding: 3 }}>{c}</td>)}</tr>)}
            </tbody>
          </table>
          <span className="absolute -top-4 left-0 text-[8px] font-semibold text-indigo-500">AUTO · {marks ? "Marks" : "Schedule"}</span>
          {selected && cornerHandles(el)}
        </div>
      );
    }
    const ts = tableSel && tableSel.id === el.id ? { rA: Math.min(tableSel.r0, tableSel.r1), rB: Math.max(tableSel.r0, tableSel.r1), cA: Math.min(tableSel.c0, tableSel.c1), cB: Math.max(tableSel.c0, tableSel.c1) } : null;
    return (
      <div key={el.id} data-testid={`cv-el-${el.id}`} style={{ position: "absolute", left: el.x, top: el.y, width: el.w, height: el.h, outline: selected ? "1.5px solid #6366f1" : "none", boxSizing: "border-box" }} onClick={(e) => { e.stopPropagation(); if (!preview) setSel(el.id); }}>
        <table style={{ width: "100%", height: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <tbody>
            {Array.from({ length: el.rows }).map((_, r) => (
              <tr key={r}>
                {Array.from({ length: el.cols }).map((_, c) => {
                  const cell = (el.cells || {})[`${r}_${c}`] || {};
                  if (cell.hidden) return null;
                  const inSel = ts && r >= ts.rA && r <= ts.rB && c >= ts.cA && c <= ts.cB;
                  return (
                    <td key={c} colSpan={cell.cs || 1} rowSpan={cell.rs || 1}
                      style={{ border: el.border ? "1px solid #334155" : "1px solid transparent", padding: 3, fontSize: 11, verticalAlign: "top", background: inSel ? "#c7d2fe" : "transparent", outline: "none" }}
                      contentEditable={!preview} suppressContentEditableWarning
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); if (!preview) onCellClick(el.id, r, c, e.shiftKey); }}
                      onBlur={(e) => setCellText(el.id, r, c, e.target.innerText)}>
                      {cell.text || ""}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {selected && <span title="Move" onMouseDown={(e) => startDrag(e, el, "move")} style={{ position: "absolute", left: -9, top: -9, width: 16, height: 16, background: "#6366f1", borderRadius: 3, cursor: "move" }} />}
        {selected && cornerHandles(el)}
      </div>
    );
  };

  const renderEl = (el) => {
    if (el.type === "table") return renderTable(el);
    const common = { position: "absolute", left: el.x, top: el.y, width: el.w, height: el.h };
    const selected = el.id === sel && !preview;
    const box = (children, extra = {}) => (
      <div key={el.id} data-testid={`cv-el-${el.id}`} onMouseDown={(e) => startDrag(e, el, "move")}
        onClick={(e) => { e.stopPropagation(); if (!preview) setSel(el.id); }}
        style={{ ...common, ...extra, cursor: preview ? "default" : "move", outline: selected ? "1.5px solid #6366f1" : "none", boxSizing: "border-box" }}>
        {children}
        {selected && cornerHandles(el)}
      </div>
    );
    if (el.type === "text") {
      return box(
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: el.align === "center" ? "center" : el.align === "right" ? "flex-end" : "flex-start", fontSize: el.fontSize, color: el.color, fontWeight: el.weight || (el.bold ? 700 : 400), fontStyle: el.italic ? "italic" : "normal", textDecoration: el.underline ? "underline" : "none", fontFamily: el.fontFamily, textAlign: el.align, letterSpacing: `${el.letterSpacing || 0}px`, textShadow: EFFECT(el), lineHeight: 1.2, overflow: "hidden", padding: "0 2px" }}>
          {valueOf(el)}
        </div>
      );
    }
    if (el.type === "rect") return box(null, { background: el.fill, border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor}` : "none", borderRadius: el.radius });
    if (el.type === "circle") return box(null, { background: el.fill, border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor}` : "none", borderRadius: "50%" });
    if (el.type === "line") return box(null, { background: el.fill });
    if (el.type === "image") {
      const label = el.fieldImage ? humanize(el.fieldImage) : "Image";
      const isQr = el.fieldImage === "qr_code" || el.fieldImage === "attendance_qr";
      return box(
        el.src ? <img src={el.src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          : isQr ? <img src={QR_SRC(`${window.location.origin}/verify/sample`)} alt="QR" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: preview ? "#e2e8f0" : "#f1f5f9", border: "1px dashed #94a3b8", fontSize: 9, color: "#64748b", textAlign: "center", padding: 2 }}>{label}</div>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-slate-100" data-testid="canvas-designer-page">
      {/* top bar */}
      <div className="flex items-center gap-3 px-3 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
        <button onClick={() => nav("/templates")} className="flex items-center gap-1 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-1.5 text-sm font-medium" data-testid="cv-back"><ChevronLeft size={16} />Templates</button>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Untitled design — name it here" className="max-w-xs bg-white/10 border-white/20 text-white placeholder:text-white/60" data-testid="cv-name" />
        <div className="ml-auto flex items-center gap-2 text-sm">
          <span className="text-white/80">Category</span>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-40 bg-white text-slate-800 h-9" data-testid="cv-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <span className="text-white/80 ml-1">Card size (mm)</span>
          <Select value={`${size.w}x${size.h}`} onValueChange={(v) => { const p = SIZES.find((s) => `${s.w}x${s.h}` === v); if (p) setSize({ w: p.w, h: p.h }); }}>
            <SelectTrigger className="w-56 bg-white text-slate-800 h-9" data-testid="cv-size"><SelectValue placeholder="Custom" /></SelectTrigger>
            <SelectContent>{SIZES.map((s) => <SelectItem key={s.label} value={`${s.w}x${s.h}`}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" value={size.w} onChange={(e) => setSize((s) => ({ ...s, w: +e.target.value || 1 }))} className="w-16 bg-white text-slate-800 h-9" data-testid="cv-w" />
          <Input type="number" value={size.h} onChange={(e) => setSize((s) => ({ ...s, h: +e.target.value || 1 }))} className="w-16 bg-white text-slate-800 h-9" data-testid="cv-h" />
          <Button onClick={save} disabled={saving} className="gap-1.5 bg-white text-indigo-700 hover:bg-white/90 h-9" data-testid="cv-save"><Save size={16} />{saving ? "Saving..." : "Save"}</Button>
        </div>
      </div>

      {/* toolbar */}
      <div className="flex items-center gap-1 px-3 py-1.5 bg-white border-b border-slate-200">
        <div className="flex rounded-lg overflow-hidden border border-slate-200 mr-2">
          {["front", "back"].map((s) => (
            <button key={s} onClick={() => { setSide(s); setSel(null); }} className={`px-3 py-1.5 text-sm font-medium capitalize ${side === s ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`cv-side-${s}`}>{s}</button>
          ))}
        </div>
        <ToolBtn t="Text" onClick={addText} testid="cv-add-text"><Type size={16} /><span className="text-sm">Text</span></ToolBtn>
        <ToolBtn t="Rectangle" onClick={addRect} testid="cv-add-rect"><Square size={16} /></ToolBtn>
        <ToolBtn t="Circle" onClick={addCircle} testid="cv-add-circle"><CircleIcon size={16} /></ToolBtn>
        <ToolBtn t="Line" onClick={addLine} testid="cv-add-line"><Minus size={16} /></ToolBtn>
        <ToolBtn t="Image" onClick={() => addImage("")} testid="cv-add-image"><ImageIcon size={16} /><span className="text-sm">Image</span></ToolBtn>
        <Popover onOpenChange={(o) => !o && setTgrid({ r: 0, c: 0 })}>
          <PopoverTrigger asChild>
            <button type="button" title="Table" onMouseDown={(e) => e.preventDefault()} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-slate-600 hover:bg-slate-100" data-testid="cv-tool-table"><Table2 size={16} /></button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <div className="text-xs text-slate-500 mb-2 text-center">{tgrid.r && tgrid.c ? `${tgrid.r} × ${tgrid.c}` : "Select size"}</div>
            <div className="inline-grid gap-0.5" style={{ gridTemplateColumns: "repeat(8, 18px)" }} onMouseLeave={() => setTgrid({ r: 0, c: 0 })}>
              {Array.from({ length: 6 }).map((_, ri) =>
                Array.from({ length: 8 }).map((_, ci) => (
                  <button key={`${ri}-${ci}`} type="button" onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setTgrid({ r: ri + 1, c: ci + 1 })} onClick={() => addTable(ri + 1, ci + 1)}
                    className={`w-[18px] h-[18px] rounded-sm border ${ri < tgrid.r && ci < tgrid.c ? "bg-indigo-400 border-indigo-500" : "bg-slate-50 border-slate-200"}`} data-testid={`cv-table-cell-${ri + 1}-${ci + 1}`} />
                ))
              )}
            </div>
            <div className="flex gap-1.5 mt-3 pt-2 border-t border-slate-100">
              <button type="button" onClick={toggleTableBorder} className="flex-1 text-xs rounded-md border border-slate-200 px-2 py-1.5 text-slate-600 hover:bg-slate-100" data-testid="cv-table-borders">Show/Hide border</button>
              <button type="button" onClick={mergeTableCells} className="flex-1 text-xs rounded-md border border-slate-200 px-2 py-1.5 text-slate-600 hover:bg-slate-100" data-testid="cv-table-merge">Merge cells</button>
            </div>
          </PopoverContent>
        </Popover>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolBtn t="Undo" onClick={undo} testid="cv-undo"><Undo2 size={16} /></ToolBtn>
        <ToolBtn t="Redo" onClick={redo} testid="cv-redo"><Redo2 size={16} /></ToolBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolBtn t="Duplicate" onClick={() => sel && duplicateEl(sel)} testid="cv-dup"><Copy size={16} /></ToolBtn>
        <ToolBtn t="Delete" onClick={() => sel && deleteEl(sel)} testid="cv-del"><Trash2 size={16} /></ToolBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolBtn t="Center horizontally" onClick={centerH} testid="cv-center-h"><AlignCenterHorizontal size={16} /></ToolBtn>
        <ToolBtn t="Center vertically" onClick={centerV} testid="cv-center-v"><AlignCenterVertical size={16} /></ToolBtn>
        <ToolBtn t="Bring forward" onClick={() => reorder(1)} testid="cv-forward"><BringToFront size={16} /></ToolBtn>
        <ToolBtn t="Send backward" onClick={() => reorder(-1)} testid="cv-backward"><SendToBack size={16} /></ToolBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <button onClick={() => setPreview((p) => !p)} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm ${preview ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="cv-preview"><Eye size={16} />Preview</button>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => setShowGrid((g) => !g)} title="Show/Hide gridlines" className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm ${showGrid ? "bg-slate-200 text-slate-700" : "text-slate-600 hover:bg-slate-100"}`} data-testid="cv-grid-toggle"><Grid3x3 size={16} /></button>
          <ToolBtn t="Zoom out" onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.2).toFixed(2)))} testid="cv-zoom-out"><ZoomOut size={16} /></ToolBtn>
          <span className="text-sm text-slate-500 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <ToolBtn t="Zoom in" onClick={() => setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)))} testid="cv-zoom-in"><ZoomIn size={16} /></ToolBtn>
          <ToolBtn t="Fit" onClick={() => setZoom(1.6)} testid="cv-zoom-fit"><Maximize2 size={16} /></ToolBtn>
        </div>
      </div>

      {/* body */}
      <div className="flex flex-1 min-h-0">
        {/* left panel */}
        <div className="w-72 bg-white border-r border-slate-200 flex flex-col">
          <div className="flex border-b border-slate-200">
            {[["fields", "Fields"], ["elements", "Elements"], ["uploads", "Uploads"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`flex-1 py-2.5 text-sm font-medium ${tab === k ? "text-indigo-600 border-b-2 border-indigo-600" : "text-slate-500"}`} data-testid={`cv-tab-${k}`}>{l}</button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            {tab === "fields" && (
              <>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Start with a layout</p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[["photo-top", "Photo on top", IdCard], ["photo-left", "Photo on left", IdCard], ["header", "Coloured header", Square]].map(([k, l, I]) => (
                    <button key={k} onClick={() => applyLayout(k)} className="flex flex-col items-center gap-1 rounded-lg border border-slate-200 p-2 text-[11px] text-slate-600 hover:border-indigo-400 hover:bg-indigo-50" data-testid={`cv-layout-${k}`}><I size={18} className="text-indigo-500" />{l}</button>
                  ))}
                </div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Add a block</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {[["name-class", "Name + class"], ["id-details", "ID details"], ["photo-name", "Photo + name"], ["qr-caption", "QR + caption"]].map(([k, l]) => (
                    <button key={k} onClick={() => addBlock(k)} className="rounded-lg border border-slate-200 px-2 py-2 text-xs text-slate-600 hover:border-indigo-400 hover:bg-indigo-50" data-testid={`cv-block-${k}`}>{l}</button>
                  ))}
                </div>
                <div className="relative mb-3">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fields..." className="pl-9 h-9" data-testid="cv-field-search" />
                </div>
                <div className="space-y-4">
                  {filtered.map((g) => {
                    const Ico = ICONS[g.icon] || Code;
                    return (
                      <div key={g.title}>
                        <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${g.color}`}><Ico size={13} />{g.title}</div>
                        <div className="space-y-1">
                          {g.tags.map((t) => (
                            <button key={t} onClick={() => addField(t)} className="w-full flex items-center justify-between rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700 hover:border-indigo-400 hover:bg-indigo-50 group" data-testid={`cv-field-${t}`}>
                              <span className="flex items-center gap-2">{IMG_FIELDS.includes(t) ? <ImageIcon size={13} className="text-slate-400" /> : <Type size={13} className="text-slate-400" />}{humanize(t)}</span>
                              <Plus size={14} className="text-slate-300 group-hover:text-indigo-500" />
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {tab === "elements" && (
              <div className="grid grid-cols-2 gap-2">
                {[["Text", addText, Type], ["Rectangle", addRect, Square], ["Circle", addCircle, CircleIcon], ["Line", addLine, Minus], ["Image", () => addImage(""), ImageIcon], ["Table", () => addTable(3, 3), Table2], ["Marks Table", () => addDynTable("marks"), Table2], ["Schedule Table", () => addDynTable("schedule"), Table2]].map(([l, fn, I]) => (
                  <button key={l} onClick={fn} className="flex flex-col items-center gap-1.5 rounded-lg border border-slate-200 p-4 text-xs text-slate-600 hover:border-indigo-400 hover:bg-indigo-50" data-testid={`cv-elem-${l.toLowerCase()}`}><I size={20} className="text-indigo-500" />{l}</button>
                ))}
              </div>
            )}
            {tab === "uploads" && (
              <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-8 text-sm text-slate-500 cursor-pointer hover:border-indigo-400" data-testid="cv-upload-drop">
                <UploadCloud size={28} className="text-slate-400" />Click to upload an image<span className="text-xs text-slate-400">PNG, JPG · Max 3MB</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} data-testid="cv-upload-input" />
              </label>
            )}
          </div>
        </div>

        {/* canvas */}
        <div className={`flex-1 overflow-auto flex p-8 ${showGrid ? "bg-[radial-gradient(circle,#cbd5e1_1px,transparent_1px)] [background-size:16px_16px]" : "bg-slate-100"}`} onMouseDown={() => { setSel(null); setTableSel(null); }} data-testid="cv-canvas-area">
          <div style={{ width: baseW * zoom, height: baseH * zoom, margin: "auto" }}>
            <div style={{ width: baseW, height: baseH, transform: `scale(${zoom})`, transformOrigin: "top left", background: bg, position: "relative", boxShadow: "0 10px 30px rgba(0,0,0,.18)", borderRadius: 6 }} data-testid="cv-card" onMouseDown={(e) => e.stopPropagation()}>
              {showGrid && !preview && <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(100,116,139,.12) 1px,transparent 1px),linear-gradient(90deg,rgba(100,116,139,.12) 1px,transparent 1px)", backgroundSize: "10px 10px", pointerEvents: "none" }} />}
              {curr.map(renderEl)}
              {!preview && guides.x.map((gx, i) => <div key={`gx${i}`} style={{ position: "absolute", left: gx, top: 0, width: 1, height: baseH, background: "#ec4899", pointerEvents: "none", zIndex: 60 }} />)}
              {!preview && guides.y.map((gy, i) => <div key={`gy${i}`} style={{ position: "absolute", top: gy, left: 0, height: 1, width: baseW, background: "#ec4899", pointerEvents: "none", zIndex: 60 }} />)}
            </div>
          </div>
        </div>

        {/* right panel */}
        <div className="w-72 bg-white border-l border-slate-200 overflow-y-auto p-4 space-y-5">
          <div>
            <p className="font-heading font-bold text-slate-900 mb-2">Properties</p>
            {!selEl ? (
              <div className="text-sm text-slate-400 space-y-2">
                <p className="font-medium text-slate-600">Card background</p>
                <div className="grid grid-cols-6 gap-2">
                  {BG_SWATCHES.map((c) => <button key={c} onClick={() => setBg(c)} className={`w-8 h-8 rounded border ${bg === c ? "ring-2 ring-indigo-500" : "border-slate-200"}`} style={{ background: c }} data-testid={`cv-bg-${c}`} />)}
                </div>
                <label className="flex items-center gap-2 text-xs mt-1">Custom <input type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="h-7 w-10 rounded" data-testid="cv-bg-custom" /></label>
                <p className="pt-3">Select an element on the card to edit it.</p>
                <p className="flex items-center gap-1.5"><Eye size={13} /> "Preview" shows sample data</p>
                <p>Drag to move & resize · Arrows nudge · Del removes · Ctrl+Z / Ctrl+Y</p>
              </div>
            ) : (
              <div className="space-y-3 text-sm" data-testid="cv-props">
                {selEl.type === "text" && (
                  <>
                    {!selEl.field && <div><label className="text-xs text-slate-500">Text</label><Input value={selEl.text} onChange={(e) => updateEl(selEl.id, { text: e.target.value })} className="h-8" data-testid="cv-prop-text" /></div>}
                    {selEl.field && <p className="text-xs text-slate-500">Bound field: <b>{humanize(selEl.field)}</b></p>}
                    <div>
                      <label className="text-xs text-slate-500">Font face</label>
                      <Select value={selEl.fontFamily || "Arial"} onValueChange={(v) => updateEl(selEl.id, { fontFamily: v })}>
                        <SelectTrigger className="h-8" data-testid="cv-prop-font"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {FONT_FACES.map((f) => <SelectItem key={f} value={f}><span style={{ fontFamily: f, fontSize: 15 }}>{f}</span></SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <label className="text-xs text-slate-500">Weight</label>
                        <Select value={String(selEl.weight || (selEl.bold ? 700 : 400))} onValueChange={(v) => updateEl(selEl.id, { weight: +v })}>
                          <SelectTrigger className="h-8" data-testid="cv-prop-weight"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[[300, "Light"], [400, "Regular"], [500, "Medium"], [600, "Semibold"], [700, "Bold"], [800, "Extrabold"]].map(([w, l]) => <SelectItem key={w} value={String(w)}>{l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-24"><label className="text-xs text-slate-500">Letter spacing</label><Input type="number" step="0.5" value={selEl.letterSpacing || 0} onChange={(e) => updateEl(selEl.id, { letterSpacing: +e.target.value || 0 })} className="h-8" data-testid="cv-prop-spacing" /></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1"><label className="text-xs text-slate-500">Font size</label><Input type="number" value={selEl.fontSize} onChange={(e) => updateEl(selEl.id, { fontSize: +e.target.value || 1 })} className="h-8" data-testid="cv-prop-size" /></div>
                      <div><label className="text-xs text-slate-500">Color</label><input type="color" value={selEl.color} onChange={(e) => updateEl(selEl.id, { color: e.target.value })} className="block h-8 w-10 rounded" data-testid="cv-prop-color" /></div>
                    </div>
                    <div className="flex gap-1">
                      <PropToggle on={selEl.bold} onClick={() => updateEl(selEl.id, { bold: !selEl.bold })} testid="cv-prop-bold"><Bold size={15} /></PropToggle>
                      <PropToggle on={selEl.italic} onClick={() => updateEl(selEl.id, { italic: !selEl.italic })} testid="cv-prop-italic"><Italic size={15} /></PropToggle>
                      <PropToggle on={selEl.underline} onClick={() => updateEl(selEl.id, { underline: !selEl.underline })} testid="cv-prop-underline"><UnderlineIcon size={15} /></PropToggle>
                      <span className="w-px h-6 bg-slate-200 mx-1" />
                      {[["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]].map(([a, I]) => (
                        <PropToggle key={a} on={selEl.align === a} onClick={() => updateEl(selEl.id, { align: a })} testid={`cv-prop-align-${a}`}><I size={15} /></PropToggle>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <label className="text-xs text-slate-500">Effect</label>
                        <Select value={selEl.effect || "none"} onValueChange={(v) => updateEl(selEl.id, { effect: v })}>
                          <SelectTrigger className="h-8" data-testid="cv-prop-effect"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="shadow">Shadow</SelectItem>
                            <SelectItem value="outline">Outline</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><label className="text-xs text-slate-500">Effect color</label><input type="color" value={selEl.effectColor || "#000000"} onChange={(e) => updateEl(selEl.id, { effectColor: e.target.value })} className="block h-8 w-10 rounded" data-testid="cv-prop-effect-color" /></div>
                    </div>
                  </>
                )}
                {(selEl.type === "rect" || selEl.type === "circle" || selEl.type === "line") && (
                  <>
                    <div><label className="text-xs text-slate-500">Fill</label><input type="color" value={selEl.fill} onChange={(e) => updateEl(selEl.id, { fill: e.target.value })} className="block h-8 w-12 rounded" data-testid="cv-prop-fill" /></div>
                    {selEl.type !== "line" && <>
                      <div className="flex items-center gap-2">
                        <div className="flex-1"><label className="text-xs text-slate-500">Border width</label><Input type="number" value={selEl.borderWidth} onChange={(e) => updateEl(selEl.id, { borderWidth: +e.target.value || 0 })} className="h-8" /></div>
                        <div><label className="text-xs text-slate-500">Border</label><input type="color" value={selEl.borderColor || "#000000"} onChange={(e) => updateEl(selEl.id, { borderColor: e.target.value })} className="block h-8 w-10 rounded" /></div>
                      </div>
                      {selEl.type === "rect" && <div><label className="text-xs text-slate-500">Corner radius</label><Input type="number" value={selEl.radius} onChange={(e) => updateEl(selEl.id, { radius: +e.target.value || 0 })} className="h-8" /></div>}
                    </>}
                  </>
                )}
                {selEl.type === "image" && (
                  <>
                    <p className="text-xs text-slate-500">{selEl.fieldImage ? <>Bound image: <b>{humanize(selEl.fieldImage)}</b></> : "Static image"}</p>
                    {!selEl.fieldImage && <label className="flex items-center justify-center gap-1.5 h-9 rounded-lg border-2 border-dashed border-slate-300 text-xs text-slate-500 cursor-pointer hover:border-indigo-400"><UploadCloud size={14} />Replace image<input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = () => updateEl(selEl.id, { src: r.result }); r.readAsDataURL(f); } }} /></label>}
                    <p className="text-[10px] text-slate-400 leading-snug">Drag any corner handle on the image to resize freely.</p>
                  </>
                )}
                {selEl.type === "table" && !selEl.dynamic && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500">Grid: <b>{selEl.rows} × {selEl.cols}</b>{tableSel && tableSel.id === selEl.id ? ` · at R${tableSel.r0 + 1} C${tableSel.c0 + 1}` : ""}</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      <button type="button" onClick={() => tableRowCol("addRow")} className="text-xs rounded-md border border-slate-200 px-2 py-1.5 text-slate-600 hover:bg-slate-100" data-testid="cv-row-add">+ Row</button>
                      <button type="button" onClick={() => tableRowCol("delRow")} className="text-xs rounded-md border border-slate-200 px-2 py-1.5 text-slate-600 hover:bg-slate-100" data-testid="cv-row-del">− Row</button>
                      <button type="button" onClick={() => tableRowCol("addCol")} className="text-xs rounded-md border border-slate-200 px-2 py-1.5 text-slate-600 hover:bg-slate-100" data-testid="cv-col-add">+ Column</button>
                      <button type="button" onClick={() => tableRowCol("delCol")} className="text-xs rounded-md border border-slate-200 px-2 py-1.5 text-slate-600 hover:bg-slate-100" data-testid="cv-col-del">− Column</button>
                    </div>
                    <button type="button" onClick={mergeTableCells} className="w-full text-xs rounded-md border border-slate-200 px-2 py-1.5 text-slate-600 hover:bg-slate-100" data-testid="cv-prop-merge">Merge selected cells</button>
                    <p className="text-[10px] text-slate-400 leading-snug">Click a cell to add/remove at that spot. Shift-click a range, then Merge.</p>
                  </div>
                )}
                <div className="grid grid-cols-4 gap-1.5 pt-1">
                  {["x", "y", "w", "h"].map((k) => (
                    <div key={k}><label className="text-xs text-slate-400 uppercase">{k}</label><Input type="number" value={selEl[k]} onChange={(e) => updateEl(selEl.id, { [k]: +e.target.value || 0 })} className="h-8 px-1 text-xs" data-testid={`cv-prop-${k}`} /></div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="flex items-center gap-1.5 font-heading font-bold text-slate-900 mb-2"><Layers size={16} />Layers</p>
            {curr.length === 0 ? <p className="text-sm text-slate-400">Nothing on this side yet.</p> : (
              <div className="space-y-1">
                {[...curr].reverse().map((el) => (
                  <button key={el.id} onClick={() => setSel(el.id)} className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${sel === el.id ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`cv-layer-${el.id}`}>
                    {el.type === "text" ? <Type size={13} /> : el.type === "image" ? <ImageIcon size={13} /> : el.type === "line" ? <Minus size={13} /> : el.type === "circle" ? <CircleIcon size={13} /> : <Square size={13} />}
                    <span className="truncate">{el.type === "text" ? (el.field ? humanize(el.field) : el.text) : el.fieldImage ? humanize(el.fieldImage) : el.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const ToolBtn = ({ t, onClick, children, testid }) => (
  <button title={t} onClick={onClick} data-testid={testid} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-slate-600 hover:bg-slate-100">{children}</button>
);
const PropToggle = ({ on, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`w-8 h-8 rounded flex items-center justify-center ${on ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{children}</button>
);
